import * as logger from './logger';

export interface TokenStorageConfig {
  storageType: 'localStorage' | 'sessionStorage';
  keyPrefix?: string;
}

export class TokenManager {
  private static TENANT_ID = 'tenant_id';
  private static ACCESS_TOKEN_KEY = 'access_token';
  private static REFRESH_TOKEN_KEY = 'refresh_token';

  private refreshCallback?: () => Promise<boolean>;
  private refreshInProgress?: Promise<boolean>;
  private storage: Storage;
  private keyPrefix: string;

  constructor(config: TokenStorageConfig = { storageType: 'localStorage' }) {
    this.storage = config.storageType === 'sessionStorage' ? sessionStorage : localStorage;
    this.keyPrefix = config.keyPrefix || '';
  }

  public setRefreshCallback(callback: () => Promise<boolean>) {
    logger.debug('[ClientWireApi.TokenManager] refresh callback set');
    this.refreshCallback = callback;
  }

  public clearRefreshCallback() {
    this.refreshCallback = undefined;
  }

  private getStorageKey(key: string): string {
    return this.keyPrefix ? `${this.keyPrefix}.${key}` : key;
  }

  public clearTokens() {
    this.storage.removeItem(this.getStorageKey(TokenManager.ACCESS_TOKEN_KEY));
    this.storage.removeItem(this.getStorageKey(TokenManager.REFRESH_TOKEN_KEY));
    this.storage.removeItem(this.getStorageKey(TokenManager.TENANT_ID));
  }

  public getAccessToken(): string | undefined {
    return this.storage.getItem(this.getStorageKey(TokenManager.ACCESS_TOKEN_KEY)) || undefined;
  }

  public getRefreshToken(): string | undefined {
    return this.storage.getItem(this.getStorageKey(TokenManager.REFRESH_TOKEN_KEY)) || undefined;
  }

  public getTenantId(): string | undefined {
    return this.storage.getItem(this.getStorageKey(TokenManager.TENANT_ID)) || undefined;
  }

  public setTokens(tenantId: string, accessToken: string, refreshToken: string | undefined | null) {
    this.storage.setItem(this.getStorageKey(TokenManager.TENANT_ID), tenantId);
    this.storage.setItem(this.getStorageKey(TokenManager.ACCESS_TOKEN_KEY), accessToken);

    if (refreshToken) {
      this.storage.setItem(this.getStorageKey(TokenManager.REFRESH_TOKEN_KEY), refreshToken);
    } else {
      this.storage.removeItem(this.getStorageKey(TokenManager.REFRESH_TOKEN_KEY));
    }
  }

  /**
   * The main method to refresh tokens.
   */
  public async refreshTokens(): Promise<boolean> {
    if (this.refreshInProgress) {
      return this.refreshInProgress;
    }
    this.refreshInProgress = this.doActualRefresh().finally(() => {
      this.refreshInProgress = undefined;
    });
    return this.refreshInProgress;
  }

  private async doActualRefresh(): Promise<boolean> {
    if (!this.refreshCallback) {
      logger.debug('[ClientWireApi.TokenManager] No refresh callback set');
      return false;
    }

    try {
      const refreshCallbackResult = await this.refreshCallback();

      if (refreshCallbackResult) {
        logger.debug(
          '[ClientWireApi.TokenManager] refreshCallback reports success; new tokens should now be set'
        );
        return true;
      } else {
        logger.warn('[ClientWireApi.TokenManager] refreshCallback reports failure');
        return false;
      }
    } catch (err) {
      logger.warn('[ClientWireApi.TokenManager] Error executing refreshCallback:', err);
      return false;
    }
  }

  public hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  public static hasAccessToken(storage: Storage = localStorage, keyPrefix = ''): boolean {
    const key = keyPrefix
      ? `${keyPrefix}.${TokenManager.ACCESS_TOKEN_KEY}`
      : TokenManager.ACCESS_TOKEN_KEY;
    return !!(storage.getItem(key) || undefined);
  }
}
